import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import {
  MongoUserId,
  type MongoUserId as MongoUserIdType,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import type { AssignableUser } from "@redotech/effect-api-shared/rpc/support-ticket-related-records";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import type { RedoBadgeProps } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useAssignSupportTicketAction } from "../actions/use-support-ticket-actions";
import {
  buildAssigneeListItems,
  getUserDisplayName,
  getUserLeadingItem,
} from "../utils/assignee-list-items";
import { useUsersForSupportTicket } from "./use-assignable-users";

const searcher = new Fuse<AssignableUser>([], {
  keys: ["name", "email"],
  threshold: 0.3,
});

export interface UseAssigneeSelectorResult {
  // Data
  assigneeListItems: RedoListItem<AssignableUser | null>[];
  selectedItem: RedoListItem<AssignableUser | null> | undefined;
  canEditAssignee: boolean;

  // Badge display
  badgeText: string;
  badgeAvatar: RedoBadgeProps["avatar"];

  // Dropdown state
  dropdownOpen: boolean;
  badgeRef: HTMLElement | null;
  setBadgeRef: (ref: HTMLElement | null) => void;

  // Handlers
  handleBadgeClick: () => void;
  handleDropdownOpenChange: (open: boolean) => void;
  handleItemSelected: (user: AssignableUser | null) => void;

  // Search
  searchText: string;
  setSearchText: (text: string) => void;
}

export function useAssigneeSelector({
  supportTicketId,
  mongoAssigneeId,
}: {
  supportTicketId: SupportTicketId;
  mongoAssigneeId: MongoUserIdType | null;
}): UseAssigneeSelectorResult {
  const assignAction = useAssignSupportTicketAction();
  const [searchText, setSearchText] = useState("");
  const canEditAssignee = useHasPermission(SupportPermissions.EDIT_ASSIGNEE);
  // Slice the global users collection to just the ticket's team. The
  // ticket's accountId is resolved internally so callers only need to
  // pass `supportTicketId`.
  const assignableUsers = useUsersForSupportTicket(supportTicketId);
  const allUsers = useMemo(() => [...assignableUsers], [assignableUsers]);
  const filteredUsers = useSearch(searcher, allUsers, searchText);
  const assigneeListItems = useMemo(
    () => buildAssigneeListItems(filteredUsers),
    [filteredUsers],
  );

  const currentAssignee = useMemo(() => {
    if (!mongoAssigneeId) {
      return null;
    }
    return allUsers.find((user) => user._id === mongoAssigneeId) ?? null;
  }, [mongoAssigneeId, allUsers]);

  const selectedItem: RedoListItem<AssignableUser | null> | undefined =
    useMemo(() => {
      if (!currentAssignee) {
        return {
          type: "text",
          id: "unassigned",
          text: "Unassigned",
          value: null,
        };
      }
      const name = getUserDisplayName(currentAssignee);
      return {
        type: "text",
        id: mongoAssigneeId!,
        text: name,
        value: currentAssignee,
        leadingItem: getUserLeadingItem(currentAssignee, name),
      };
    }, [currentAssignee, mongoAssigneeId]);

  // UI state for dropdown
  const [badgeRef, setBadgeRef] = useState<HTMLElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleBadgeClick = useHandler(() => {
    if (canEditAssignee) {
      setDropdownOpen((open) => !open);
    }
  });

  const handleDropdownOpenChange = useHandler((open: boolean) => {
    setDropdownOpen(open);
    if (!open) {
      setSearchText("");
    }
  });

  const handleItemSelected = useHandler((user: AssignableUser | null) => {
    const newAssigneeId = user ? MongoUserId.make(user._id.toString()) : null;
    void assignAction.execute(supportTicketId, { assigneeId: newAssigneeId });
    setDropdownOpen(false);
  });

  // Extract avatar info for badge display
  const badgeAvatar: RedoBadgeProps["avatar"] =
    selectedItem?.leadingItem?.type === "avatar"
      ? {
          alt: selectedItem.leadingItem.alt,
          name: selectedItem.leadingItem.name,
          imageUrl: selectedItem.leadingItem.imageUrl ?? null,
        }
      : undefined;

  return {
    // Data
    assigneeListItems,
    selectedItem,
    canEditAssignee,

    // Badge display
    badgeText: selectedItem?.text || "Unassigned",
    badgeAvatar,

    // Dropdown state
    dropdownOpen,
    badgeRef,
    setBadgeRef,

    // Handlers
    handleBadgeClick,
    handleDropdownOpenChange,
    handleItemSelected,

    // Search
    searchText,
    setSearchText,
  };
}
